import Form from "@rjsf/core";
import type {
  IconButtonProps,
  RJSFSchema,
  RegistryWidgetsType,
  TemplatesType,
  TitleFieldProps,
  UiSchema,
  WidgetProps,
} from "@rjsf/utils";
import validator from "@rjsf/validator-ajv8";
import type { JSX } from "react";

import type { JsonSchema, Operation } from "../api/catalog";
import type { LookupKind } from "../api/lookup";
import { LookupField } from "./LookupField";
import { generateUlid } from "../lib/ulid";

interface SchemaFormProps {
  operation: Operation;
  value: unknown;
  onChange: (value: unknown) => void;
}

/**
 * The request-body form, generated from the operation's JSON Schema.
 *
 * This is the whole point of building the catalogue from the live schema: a field added to a
 * Memory Service write model appears here with no bench change. `overlay.yaml` contributes only
 * ordering, prefilled values and per-field widget choices.
 *
 * Deliberately the single file that touches the form library, so swapping or upgrading it is a
 * one-file change rather than a rewrite.
 */
export function SchemaForm({ operation, value, onChange }: SchemaFormProps): JSX.Element | null {
  if (!operation.body_schema) {
    return null;
  }

  return (
    <Form
      schema={simplify(operation.body_schema) as RJSFSchema}
      uiSchema={buildUiSchema(operation)}
      formData={value}
      validator={validator}
      widgets={WIDGETS}
      templates={TEMPLATES}
      onChange={(event) => onChange(event.formData)}
      liveValidate={false}
      showErrorList={false}
    />
  );
}

/**
 * Collapse `anyOf: [T, {type: "null"}]` into `T`, everywhere.
 *
 * AMS models every optional field that way, and the form library renders such a union as a type
 * picker -- so each optional field arrived with a redundant "which shape?" dropdown and its label
 * printed twice. An optional field left blank is already null, so the union carries no
 * information a tester needs.
 */
export function simplify(schema: JsonSchema | null): JsonSchema | null {
  if (schema === null) {
    return null;
  }

  const walk = (node: unknown, depth: number): unknown => {
    if (Array.isArray(node)) {
      return node.map((item) => walk(item, depth + 1));
    }
    if (!node || typeof node !== "object" || depth > 12) {
      return node;
    }

    const record = node as Record<string, unknown>;
    for (const key of ["anyOf", "oneOf"] as const) {
      const branches = record[key];
      if (!Array.isArray(branches)) {
        continue;
      }
      const meaningful = branches.filter(
        (branch) => !(branch && typeof branch === "object" && (branch as Record<string, unknown>)["type"] === "null"),
      );
      if (meaningful.length === 1 && meaningful.length < branches.length) {
        const { [key]: _dropped, ...rest } = record;
        return walk({ ...(meaningful[0] as Record<string, unknown>), ...rest }, depth + 1);
      }
    }

    return Object.fromEntries(
      Object.entries(record).map(([key, value]) => [key, walk(value, depth + 1)]),
    );
  };

  return walk(schema, 0) as JsonSchema;
}

/**
 * Translate the overlay's ordering and hints into the form library's own uiSchema.
 *
 * `ui:lookup` is a bench concept, not a library one, so it becomes the custom `lookup` widget
 * with the kind passed through as an option. Array items get their generated per-item captions
 * suppressed -- "Summarizes-1" beside an input labelled "Summarizes" is noise.
 */
export function buildUiSchema(operation: Operation): UiSchema {
  const ui: UiSchema = {
    "ui:submitButtonOptions": { norender: true },
    ...normaliseGeneratedLabels(simplify(operation.body_schema)),
  };

  if (operation.ui_order.length > 0) {
    // "*" lets unlisted fields follow, so a newly added field is never hidden by an
    // out-of-date overlay entry.
    ui["ui:order"] = [...operation.ui_order, "*"];
  }

  for (const [field, raw] of Object.entries(operation.ui_hints)) {
    ui[field] = mergeUi(ui[field] as UiSchema | undefined, toUiSchema(raw));
  }

  return ui;
}

/** One overlay hint -> one uiSchema fragment. Supports `items` for arrays of ids. */
function toUiSchema(raw: unknown): UiSchema {
  const hint = raw as {
    "ui:widget"?: string;
    "ui:lookup"?: LookupKind;
    items?: unknown;
  };

  const fragment: UiSchema = {};
  if (hint["ui:lookup"]) {
    fragment["ui:widget"] = "lookup";
    fragment["ui:options"] = { lookupKind: hint["ui:lookup"] };
  } else if (hint["ui:widget"]) {
    fragment["ui:widget"] = hint["ui:widget"];
  }
  if (hint.items) {
    fragment["items"] = mergeUi(fragment["items"] as UiSchema | undefined, toUiSchema(hint.items));
  }
  return fragment;
}

function mergeUi(base: UiSchema | undefined, extra: UiSchema): UiSchema {
  if (!base) {
    return extra;
  }
  const merged: UiSchema = { ...base, ...extra };
  if (base["items"] || extra["items"]) {
    merged["items"] = {
      ...(base["items"] as UiSchema | undefined),
      ...(extra["items"] as UiSchema | undefined),
    };
  }
  return merged;
}

/**
 * Mirror the schema's shape, fixing two things the generated labels get wrong.
 *
 * Array items are captioned "Summarizes-1", "Summarizes-2" beneath a fieldset already titled
 * "Summarizes", which reads as a broken form rather than a list. And nested objects are captioned
 * with AMS's own Pydantic class names -- "SummaryPayload" -- where the property name is what a
 * tester recognises. Both are corrected here rather than in the title template, because this is
 * where the real property names are.
 */
function normaliseGeneratedLabels(schema: JsonSchema | null, depth = 0): UiSchema {
  if (!schema || depth > 4) {
    return {};
  }
  const defs = (schema["$defs"] ?? {}) as Record<string, JsonSchema>;

  const resolve = (node: unknown, seen = 0): JsonSchema | null => {
    if (!node || typeof node !== "object" || seen > 4) {
      return null;
    }
    const candidate = node as JsonSchema;
    const ref = candidate["$ref"];
    if (typeof ref === "string" && ref.startsWith("#/$defs/")) {
      return resolve(defs[ref.slice("#/$defs/".length)], seen + 1);
    }
    // AMS models optional values as anyOf: [{...}, {"type": "null"}].
    for (const branch of (candidate["anyOf"] ?? candidate["oneOf"] ?? []) as unknown[]) {
      const resolved = resolve(branch, seen + 1);
      if (resolved && resolved["type"] !== "null") {
        return resolved;
      }
    }
    return candidate;
  };

  const walk = (node: unknown, seen: number): UiSchema => {
    const resolved = resolve(node, 0);
    if (!resolved || seen > 4) {
      return {};
    }

    if (resolved["type"] === "array" && resolved["items"]) {
      return {
        items: { "ui:options": { label: false }, ...walk(resolved["items"], seen + 1) },
      };
    }

    const properties = resolved["properties"] as Record<string, unknown> | undefined;
    if (!properties) {
      return {};
    }
    const fragment: UiSchema = {};
    for (const [name, child] of Object.entries(properties)) {
      const nested = walk(child, seen + 1);
      const resolvedChild = resolve(child, 0);
      const childTitle = resolvedChild?.["title"];
      if (typeof childTitle === "string" && looksLikeAModelName(childTitle)) {
        nested["ui:title"] = humanise(name);
      }
      if (Object.keys(nested).length > 0) {
        fragment[name] = nested;
      }
    }
    return fragment;
  };

  return walk(schema, depth);
}

/** Pydantic class names arrive as titles; "Blob ref" is what a tester should read. */
function looksLikeAModelName(title: string): boolean {
  return /^[A-Z][A-Za-z0-9]*$/.test(title) && !/\s/.test(title);
}

function humanise(name: string): string {
  const words = name.split("_").filter(Boolean);
  if (words.length === 0) {
    return name;
  }
  const [first, ...rest] = words;
  return [first!.charAt(0).toUpperCase() + first!.slice(1), ...rest].join(" ");
}

/** Starting value for a body form, before any example is picked: the overlay's defaults. */
export function initialBody(operation: Operation): unknown {
  if (!operation.body_schema) {
    return undefined;
  }
  return Object.keys(operation.defaults).length > 0
    ? structuredClone(operation.defaults)
    : {};
}

/** A ULID field with a generate button, so a tester never has to invent one. */
function UlidWidget({ value, onChange, id, required }: WidgetProps): JSX.Element {
  return (
    <span className="input-row">
      <input
        id={id}
        value={typeof value === "string" ? value : ""}
        required={required}
        onChange={(event) => onChange(event.target.value)}
      />
      <button type="button" onClick={() => onChange(generateUlid())}>
        Generate
      </button>
    </span>
  );
}

/** An entity, memory or repository picker driven by the overlay's `ui:lookup`. */
function LookupWidget({ value, onChange, options, required }: WidgetProps): JSX.Element {
  const kind = (options?.["lookupKind"] as LookupKind | undefined) ?? "entity";
  return (
    <LookupField
      kind={kind}
      value={typeof value === "string" ? value : ""}
      onChange={onChange}
      // The form library types `required` as optional, and exactOptionalPropertyTypes will not
      // pass an explicit undefined into a boolean prop.
      required={required ?? false}
    />
  );
}

/** A raw JSON editor, for the free-form objects the service does not constrain. */
function JsonWidget({ value, onChange, id }: WidgetProps): JSX.Element {
  return (
    <textarea
      id={id}
      className="json-editor"
      rows={8}
      value={typeof value === "string" ? value : JSON.stringify(value ?? {}, null, 2)}
      onChange={(event) => {
        try {
          onChange(JSON.parse(event.target.value));
        } catch {
          // Keep the text as typed while it is mid-edit and not yet valid JSON.
          onChange(event.target.value);
        }
      }}
    />
  );
}

const WIDGETS: RegistryWidgetsType = {
  ulid: UlidWidget,
  lookup: LookupWidget,
  json: JsonWidget,
};

/**
 * Array controls with words on them.
 *
 * The library's own buttons render a Bootstrap glyphicon `<i>` and nothing else, so without
 * Bootstrap loaded they appear as blank grey boxes -- unusable, and the reason a list of ids
 * looked like a broken form.
 */
function labelledButton(label: string, title: string, className: string) {
  return function LabelledButton({
    onClick,
    disabled,
    ...rest
  }: IconButtonProps): JSX.Element {
    return (
      <button
        type="button"
        className={className}
        title={title}
        aria-label={title}
        onClick={onClick}
        disabled={disabled}
        {...(rest["id"] ? { id: String(rest["id"]) } : {})}
      >
        {label}
      </button>
    );
  };
}

/**
 * Fieldset captions.
 *
 * The root needs none -- the panel header already names the operation -- and every other title
 * has already been corrected in the uiSchema, where the property names live.
 */
function TitleField({ id, title, required }: TitleFieldProps): JSX.Element | null {
  if (id === "root__title") {
    return null;
  }
  return (
    <legend id={id} className="form-legend">
      {title}
      {required && <span className="form-legend__required"> required</span>}
    </legend>
  );
}

const TEMPLATES: Partial<TemplatesType> = {
  TitleFieldTemplate: TitleField,
  ButtonTemplates: {
    SubmitButton: () => null,
    AddButton: labelledButton("+ Add", "Add an entry", "array-btn array-btn--add"),
    RemoveButton: labelledButton("Remove", "Remove this entry", "array-btn array-btn--remove"),
    MoveUpButton: labelledButton("↑", "Move up", "array-btn"),
    MoveDownButton: labelledButton("↓", "Move down", "array-btn"),
    CopyButton: labelledButton("Duplicate", "Duplicate this entry", "array-btn"),
  },
};
